import { useEffect, useRef, useCallback } from 'react';

const IDLE_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * Calls `onIdle` after `timeoutMs` of inactivity.
 * Calls `onWarn` at `(timeoutMs - warnBeforeMs)` as an advance warning.
 * Calls `onActivity` whenever user interaction resets the timer (useful for
 * dismissing any warning UI).
 *
 * All timers reset on any user interaction event.
 */
export function useIdleTimeout(
  onIdle: () => void,
  timeoutMs = 5 * 60 * 1000,
  enabled = true,
  onWarn?: () => void,
  warnBeforeMs = 60 * 1000,
  onActivity?: () => void,
): void {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef     = useRef(onIdle);
  const onWarnRef     = useRef(onWarn);
  const onActivityRef = useRef(onActivity);

  useEffect(() => { onIdleRef.current     = onIdle;     }, [onIdle]);
  useEffect(() => { onWarnRef.current     = onWarn;     }, [onWarn]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);

  const reset = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);

    // Schedule the advance warning
    if (onWarnRef.current && warnBeforeMs > 0 && warnBeforeMs < timeoutMs) {
      warnTimerRef.current = setTimeout(() => {
        onWarnRef.current?.();
      }, timeoutMs - warnBeforeMs);
    }

    // Schedule the sign-out
    idleTimerRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeoutMs);
  }, [timeoutMs, warnBeforeMs]);

  useEffect(() => {
    if (!enabled) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      return;
    }

    reset();

    const handleActivity = () => {
      onActivityRef.current?.();
      reset();
    };

    IDLE_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      IDLE_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [enabled, reset]);
}
